import { useMemo } from "react"
import {
  AlertCircle,
  Block,
  Button,
  Checkbox,
  CheckCircle,
  Input,
  LabelLarge,
  Link,
  ParagraphMedium,
  ParagraphSmall,
  Spinner,
  useStyletron,
  XCircle,
} from "@mezo-org/mezo-clay"
import { DISALLOWED_MEZO_ID_PATTERNS } from "@mezo-org/passport"

import { links, routes } from "#/shared/constants"
import { useCreateAccountModal, useCreateWalletAccount } from "#/shared/hooks"

import OnboardingModalContent from "./OnboardingModalContent"
import OnboardingModalReward from "./OnboardingModalReward"

export default function OnboardingModalCreateAccount() {
  const {
    username,
    usernameInvalid,
    usernameTaken,
    showInvalidUsernameInfo,
    updateUsernameInput,
    termsAccepted,
    setTermsAccepted,
    isCreateAccountButtonDisabled,
    setIsInputActive,
    setIsInputBlurred,
    usernameErrors,
    isValidationFetching,
  } = useCreateAccountModal()

  const { createWalletAccount } = useCreateWalletAccount()

  const [css, theme] = useStyletron()

  const inputEndEnhancer = useMemo(() => {
    if (username) {
      if (isValidationFetching) return <Spinner $size="small" />
      return showInvalidUsernameInfo ? (
        <XCircle color={theme.colors.contentNegative} />
      ) : (
        <CheckCircle color={theme.colors.contentPositive} />
      )
    }

    return null
  }, [showInvalidUsernameInfo, theme.colors, username, isValidationFetching])

  return (
    <OnboardingModalContent
      header="Choose your username"
      footer={
        <Block
          className={css({
            width: "100%",
            display: "flex",
            flexDirection: "column",
            gap: theme.sizing.scale800,
          })}
        >
          <OnboardingModalReward>Complete to claim</OnboardingModalReward>
          <Checkbox
            checked={termsAccepted}
            onChange={() => setTermsAccepted((prev) => !prev)}
            overrides={{
              Root: {
                style: () => ({
                  display: "flex",
                  alignItems: "center",
                  gap: theme.sizing.scale400,
                }),
              },
            }}
          >
            <ParagraphMedium className={css({ textAlign: "left" })}>
              By creating an account you agree to{" "}
              <Link
                href={links.TERMS_OF_USE}
                target="_blank"
                className={css({ display: "inline-block" })}
              >
                Mezo&apos;s Terms of Use
              </Link>{" "}
              and{" "}
              <Link
                href={routes.PRIVACY}
                target="_blank"
                className={css({ display: "inline-block" })}
              >
                Privacy Policy
              </Link>
              .
            </ParagraphMedium>
          </Checkbox>

          <Button
            size="large"
            onClick={createWalletAccount}
            disabled={isCreateAccountButtonDisabled}
            width="100%"
          >
            Create account
          </Button>
        </Block>
      }
    >
      <LabelLarge marginBottom={theme.sizing.scale300}>Username</LabelLarge>
      <Input
        value={username}
        onChange={(e) => updateUsernameInput(e.target.value)}
        onBlur={() => {
          setIsInputBlurred(true)
        }}
        onFocus={() => setIsInputActive(true)}
        error={showInvalidUsernameInfo}
        endEnhancer={inputEndEnhancer}
      />
      <Block
        className={css({
          marginTop: theme.sizing.scale300,
          display: "flex",
          gap: theme.sizing.scale100,
        })}
      >
        {showInvalidUsernameInfo && (
          <Block marginTop={theme.sizing.scale0}>
            <AlertCircle size={14} color={theme.colors.contentNegative} />
          </Block>
        )}
        <Block>
          {usernameInvalid && (
            <ParagraphSmall color={theme.colors.contentNegative}>
              Invalid format.
            </ParagraphSmall>
          )}
          {usernameTaken && !usernameInvalid && !!username && (
            <ParagraphSmall color={theme.colors.contentNegative}>
              Username taken.
            </ParagraphSmall>
          )}
        </Block>
      </Block>
      <Block>
        {/* TODO: Get these from passport lib */}
        {DISALLOWED_MEZO_ID_PATTERNS.map(({ error }) => (
          <ParagraphSmall
            key={error}
            color={
              showInvalidUsernameInfo &&
              usernameErrors?.some((userError) => userError === error)
                ? theme.colors.contentNegative
                : theme.colors.contentTertiary
            }
          >
            - {error}
          </ParagraphSmall>
        ))}
      </Block>
    </OnboardingModalContent>
  )
}
